/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { Section, OpeningHour } from "types/models/menu"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import dayjs from "dayjs"
import {
  SectionData,
  ServiceHours,
  ServiceDay,
  GroupSellingSchedule,
} from "types/api"
import { isEmpty, find, pick } from "lodash"

// order of days returned by BE
export const daysMap = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

// order of days to display
export const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

export const countSectionItems: (
  section?: Section | null,
) => number = section =>
  section?.categories?.reduce(
    (acc, category) => acc + (category?.items?.length ?? 0),
    0,
  ) ?? 0

export const ConfirmDeleteSectionOptions = (section: Section) => ({
  title: useTranslation("menu.modals.deleteSection.title"),
  content: useTranslation("menu.modals.deleteSection.content", {
    "<x>": `${countSectionItems(section)}`,
    "<y>": `${section?.categories?.length ?? 0}`,
  }),
  okText: useTranslation("menu.buttons.delete"),
  cancelText: useTranslation("menu.buttons.cancel"),
  okButtonProps: {
    danger: true,
  },
})

export const DaysText = () => {
  const translationsMap = days.reduce(
    (acc: { [key: string]: string }, day) => ({
      ...acc,
      [day]: `common.days.${day}`,
    }),
    {},
  )
  return useTranslationsMap(translationsMap)
}

export enum openPeriodTypes {
  OPEN_ALL_DAY = "OpenAllDay",
  CLOSED_ALL_DAY = "ClosedAllDay",
  PERIOD = "Period",
}

export const openPeriodTranslationKeys: {
  [openPeriodType: string]: string
} = {
  [openPeriodTypes.PERIOD]: "menu.section.openPeriodTypes.customRange",
  [openPeriodTypes.CLOSED_ALL_DAY]:
    "menu.section.openPeriodTypes.unavailableAllDay",
  [openPeriodTypes.OPEN_ALL_DAY]:
    "menu.section.openPeriodTypes.availableAllDay",
}

export const OpenPeriodText = () =>
  useTranslationsMap(openPeriodTranslationKeys)

export const availableStatusesText: {
  [availableStatus: number]: string
} = {
  0: "PENDING",
  1: "ACTIVE",
  2: "DEACTIVATED",
  3: "ARCHIVED",
  4: "SOFTDELETED",
}

export const mapSectionObj: (section: Section) => SectionData = section => ({
  sectionID: section?.sectionID ?? "",
  merchantID: "",
  name: section?.sectionName ?? "",
  cmplxSortOrder: section?.sortOrder ?? 0,
  status: availableStatusesText[section?.availableStatus ?? 0],
  // externalID: "",
})

export const getOpenPeriodType = (openingHour?: OpeningHour) => {
  if (isEmpty(openingHour?.ranges)) return openPeriodTypes.CLOSED_ALL_DAY

  if (
    find(openingHour?.ranges, {
      start: "00:00",
      end: "24:00",
    })
  ) {
    return openPeriodTypes.OPEN_ALL_DAY
  }

  return openPeriodTypes.PERIOD
}

export const mapServiceDay: (openingHour: OpeningHour) => ServiceDay = (
  openingHour: OpeningHour,
) => {
  const openPeriodType = getOpenPeriodType(openingHour)
  const obj: any = {
    openPeriodType,
  }

  if (openPeriodType === openPeriodTypes.PERIOD) {
    obj.periods = openingHour.ranges.map(({ start, end }) => ({
      startTime: start,
      endTime: end,
    }))
  }
  return obj
}

export const mapServiceHours: (
  openingHours: OpeningHour[],
) => ServiceHours = openingHours =>
  openingHours?.reduce((acc, openingHour, index) => {
    return {
      ...acc,
      [daysMap[index]?.toLowerCase()]: mapServiceDay(openingHour),
    }
  }, {}) ?? {}

const dayRanges = (ranges: number[]) => {
  const daysText = DaysText()

  const words = ranges
    .reduce((acc: any[], curr) => {
      if (acc.length === 0) {
        acc.push([curr])
        return acc
      }
      const r = acc[acc.length - 1]
      if (curr - r[r.length - 1] === 1) r.push(curr)
      else acc.push([curr])
      return acc
    }, [])
    .map((range: number[]) => {
      const startDay = days[range[0]]
      const endDay = days[range[range.length - 1]]

      return range.length === 1
        ? daysText[startDay]
        : `${daysText[startDay]}-${daysText[endDay]}`
    })
  return words
}

const groupPeriods = (openingHours: OpeningHour[]) => {
  const timeFormatA = "HH:mm"
  const timeFormatB = "HH:mm"

  const mapPeriods: {
    [key: string]: string[]
  } = {}

  openingHours?.forEach((openingHour, index) => {
    const day = daysMap[index]

    openingHour.ranges.forEach(range => {
      const start = dayjs(range.start, timeFormatA).format(timeFormatB)
      let end = dayjs(range.end, timeFormatA).format(timeFormatB)

      end = end === "00:00" ? "24:00" : end

      const key = `${start}-${end}`

      mapPeriods[key] = [...(mapPeriods[key] ?? []), day]
    })
  })
  return mapPeriods
}

export const getPeriods = (openingHours: OpeningHour[]) => {
  const mapPeriods = groupPeriods(openingHours)
  const mapDays: { [key: string]: string[] } = {}

  Object.keys(mapPeriods).forEach(period => {
    const daysAvailable = mapPeriods[period]

    const ranges = daysAvailable.map(day => days.indexOf(day)).sort()

    const dayRange = dayRanges(ranges)

    dayRange.forEach(word => {
      mapDays[word] = [...(mapDays[word] ?? []), period]
    })
  })

  return mapDays
}

export const getSellingSchedules = (openingHours: OpeningHour[]) => {
  const mapPeriods = groupPeriods(openingHours)
  const mapDays: GroupSellingSchedule[] = []
  let arrayKey = 0

  Object.keys(mapPeriods).forEach(period => {
    const daysAvailable = mapPeriods[period]

    const ranges = daysAvailable.map(day => days.indexOf(day)).sort()

    const dayRange = dayRanges(ranges)

    mapDays.push({
      key: arrayKey,
      days: dayRange,
      periods: [period],
    })

    arrayKey += 1
  })

  return mapDays
}

export const cleanServiceHours: (
  serviceHours: ServiceHours,
) => ServiceHours = serviceHours => {
  const cleanData: ServiceHours = {}
  days.forEach(day => {
    const serviceDay = serviceHours[day]
    const pickFields = ["openPeriodType"]
    if (serviceDay.openPeriodType === openPeriodTypes.PERIOD) {
      pickFields.push("periods")
    }
    // @ts-ignore
    cleanData[day] = pick(serviceDay, pickFields)
  })

  return cleanData
}
